/**
 * SimuladorRefatorado.jsx  —  v3.1
 * Centro de Cotação & Despacho
 *
 * Mudanças sobre a v3.0:
 *  • PREÇOS → CALCULADORA: o antigo "Gerenciador de Preços" (CRUD de regras
 *    em /api/orcamentos/precos) foi REMOVIDO. A precificação agora é feita
 *    100% pela CalculadoraPreco que já existe no sistema.
 *  • A COTAÇÃO OLHA PARA A CALCULADORA: o valor e o detalhamento financeiro
 *    vêm do que o operador define/aplica na calculadora (bandeirada, km
 *    excedente, espera, adicionais, origem do cliente, margem etc.).
 *  • A distância da rota calculada no mapa é injetada na calculadora (kmInicial).
 *  • Reorganização da coluna de Endereços (rua em linha cheia, CEP + Nº abaixo).
 *  • Contraste dos rótulos corrigido no CSS (sem letras esmaecidas).
 */

import React, {
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select from 'react-select';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import api from '../services/api';
import EnviarModal from './EnviarModal';
import CalculadoraPreco from './CalculadoraPreco';
import './SimuladorRefatorado.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

import {
  FiSave,
  FiFileText,
  FiSend,
  FiRefreshCw,
  FiPlus,
  FiTrash2,
  FiUserPlus,
  FiX,
  FiNavigation,
  FiClock,
  FiDollarSign,
  FiTruck,
  FiCheckCircle,
  FiAlertCircle,
  FiMapPin,
  FiChevronRight,
  FiActivity,
  FiZap,
} from 'react-icons/fi';

// ─── Leaflet defaults ──────────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const makeColoredIcon = (hue) =>
  new L.DivIcon({
    className: '',
    html: `<div style="
      width:16px;height:16px;border-radius:50%;
      background:${hue};
      border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,.4);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const iconPartida = makeColoredIcon('#22c55e');
const iconChegada = makeColoredIcon('#f97316');

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoiYmxuYXNjaW1lbnRvMjQwMjk4IiwiYSI6ImNtZ2lnYnc1NTA5MmEycG9pM2M5M3IwcWMifQ.JcuK1f9M-yZdwCeTXDx20Q';

// ─── Helpers de formatação ──────────────────────────────────────────────────────
const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const f2 = (v) => Number(v || 0).toFixed(2);

// ─── Estado inicial ────────────────────────────────────────────────────────────
const FORM_INIT = {
  partida: '',
  cepPartida: '',
  numeroPartida: '',
  chegada: '',
  cepChegada: '',
  numeroChegada: '',
  desconto: 0,
};

const ROTA_INIT = {
  distanciaKm: 0,
  tempoMinutos: 0,
  partidaCoords: null,
  chegadaCoords: null,
  polyline: [],
};

const INITIAL_STATE = {
  form: FORM_INIT,
  rota: ROTA_INIT,
  // Detalhe BASE vindo da calculadora (antes do desconto do simulador).
  calcDetalhe: null,
  clienteSelecionado: null,
  clienteAvulso: false,
  savedQuote: null,
  clientes: [],
  historico: [],
  tenantInfo: { nome_empresa: '', email: '', telefone: '', endereco: '' },
  errors: {},
  ui: {
    loadingRota: false,
    loadingSave: false,
    loadingConverter: false,
    calcModalOpen: false,
    clientModalOpen: false,
    sendModalOpen: false,
    filaBadge: 0,
  },
};

// ─── Reducer ──────────────────────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {
    case 'SET_FORM':
      return {
        ...state,
        form: { ...state.form, ...action.payload },
        errors: { ...state.errors, ...clearErrorsFor(action.payload) },
      };
    case 'SET_ROTA':
      return { ...state, rota: action.payload };
    case 'SET_CALC_DETALHE':
      return { ...state, calcDetalhe: action.payload };
    case 'SET_CLIENTE':
      return { ...state, clienteSelecionado: action.payload, errors: { ...state.errors, cliente: null } };
    case 'SET_CLIENTE_AVULSO':
      return {
        ...state,
        clienteAvulso: action.payload,
        clienteSelecionado: action.payload ? null : state.clienteSelecionado,
        errors: { ...state.errors, cliente: null },
      };
    case 'SET_SAVED_QUOTE':
      return { ...state, savedQuote: action.payload };
    case 'SET_CLIENTES':
      return { ...state, clientes: action.payload };
    case 'SET_HISTORICO':
      return { ...state, historico: action.payload };
    case 'SET_TENANT_INFO':
      return { ...state, tenantInfo: { ...state.tenantInfo, ...action.payload } };
    case 'SET_ERRORS':
      return { ...state, errors: { ...state.errors, ...action.payload } };
    case 'SET_UI':
      return { ...state, ui: { ...state.ui, ...action.payload } };
    case 'ADD_CLIENTE_LISTA': {
      const fmt = action.payload;
      return {
        ...state,
        clientes: [...state.clientes, fmt].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
        clienteSelecionado: fmt,
        clienteAvulso: false,
        errors: { ...state.errors, cliente: null },
      };
    }
    case 'NOVA_COTACAO':
      return {
        ...INITIAL_STATE,
        clientes: state.clientes,
        historico: state.historico,
        tenantInfo: state.tenantInfo || INITIAL_STATE.tenantInfo,
        ui: { ...INITIAL_STATE.ui, filaBadge: state.ui.filaBadge },
      };
    case 'FILA_BADGE_INC':
      return { ...state, ui: { ...state.ui, filaBadge: state.ui.filaBadge + 1 } };
    case 'FILA_BADGE_RESET':
      return { ...state, ui: { ...state.ui, filaBadge: 0 } };
    default:
      return state;
  }
}

function clearErrorsFor(payload) {
  const cleared = {};
  Object.keys(payload).forEach((k) => (cleared[k] = null));
  return cleared;
}

// ─── Hook: dados iniciais ──────────────────────────────────────────────────────
function useSimuladorData(dispatch) {
  const fetchedRef = useRef(false); // evita double-fetch no StrictMode

  const fetchAll = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    try {
      const [rC, rH, rT] = await Promise.allSettled([
        api.get('/api/clients/list-for-simulator'),
        api.get('/api/orcamentos/historico'),
        api.get('/api/tenant/info'), // dados do tenant para PDF
      ]);

      if (rC.status === 'fulfilled' && Array.isArray(rC.value.data)) {
        dispatch({
          type: 'SET_CLIENTES',
          payload: rC.value.data.map((c) => ({
            value: c.id,
            label: `${c.nome}${c.cpf_cnpj ? ` · ${c.cpf_cnpj}` : ''}`,
            ...c,
          })),
        });
      }

      if (rH.status === 'fulfilled' && Array.isArray(rH.value.data)) {
        dispatch({ type: 'SET_HISTORICO', payload: rH.value.data });
      }

      if (rT.status === 'fulfilled' && rT.value.data) {
        dispatch({ type: 'SET_TENANT_INFO', payload: rT.value.data });
      }
    } catch (err) {
      console.error('[SIMULADOR DATA]', err.message);
      toast.error('Falha ao carregar dados do simulador.');
    }
  }, [dispatch]);

  const fetchHistorico = useCallback(async () => {
    try {
      const res = await api.get('/api/orcamentos/historico');
      dispatch({ type: 'SET_HISTORICO', payload: res.data });
    } catch {/* silencioso */}
  }, [dispatch]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { fetchHistorico };
}

// ─── Hook: cálculo de rota ─────────────────────────────────────────────────────
function useRotaCalculator(dispatch) {
  const loading = useRef(false);

  const calcularRota = useCallback(
    async (form) => {
      if (loading.current) return;

      // Validação local antes de bater na API
      const erros = {};
      if (!form.partida?.trim()) erros.partida = 'Endereço de partida obrigatório.';
      if (!form.numeroPartida?.trim()) erros.numeroPartida = 'Número obrigatório.';
      if (!form.chegada?.trim()) erros.chegada = 'Endereço de chegada obrigatório.';
      if (!form.numeroChegada?.trim()) erros.numeroChegada = 'Número obrigatório.';

      if (Object.keys(erros).length) {
        dispatch({ type: 'SET_ERRORS', payload: erros });
        toast.error('Preencha todos os campos de endereço.');
        return;
      }

      loading.current = true;
      dispatch({ type: 'SET_UI', payload: { loadingRota: true } });

      const montarEndereco = (base, num) => {
        const [rua, ...resto] = base.split(/,(.+)/);
        return `${rua.trim()}, ${num}, ${resto.join('').trim()}`.replace(/, ,/g, ',').trim();
      };

      const endPartida = montarEndereco(form.partida, form.numeroPartida);
      const endChegada = montarEndereco(form.chegada, form.numeroChegada);

      try {
        const geo = (end) =>
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            end
          )}.json?access_token=${MAPBOX_TOKEN}&country=BR&language=pt&limit=1`;

        const [rP, rC] = await Promise.all([
          axios.get(geo(endPartida)),
          axios.get(geo(endChegada)),
        ]);

        if (!rP.data.features?.length || !rC.data.features?.length) {
          toast.error('Endereço não encontrado. Verifique os dados e tente novamente.');
          return;
        }

        const pCoord = rP.data.features[0].center; // [lng, lat]
        const cCoord = rC.data.features[0].center;

        const dirUrl =
          `https://api.mapbox.com/directions/v5/mapbox/driving/` +
          `${pCoord.join(',')};${cCoord.join(',')}` +
          `?access_token=${MAPBOX_TOKEN}&geometries=geojson&language=pt`;

        const rDir = await axios.get(dirUrl);
        const route = rDir.data.routes?.[0];

        if (!route) {
          toast.error('Não foi possível calcular a rota entre os endereços.');
          return;
        }

        dispatch({
          type: 'SET_ROTA',
          payload: {
            distanciaKm: parseFloat((route.distance / 1000).toFixed(2)),
            tempoMinutos: Math.round(route.duration / 60),
            partidaCoords: [pCoord[1], pCoord[0]],
            chegadaCoords: [cCoord[1], cCoord[0]],
            polyline: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
          },
        });

        toast.success('Rota calculada com precisão!');
      } catch (err) {
        console.error('[ROTA]', err);
        toast.error(err.response?.data?.message || 'Falha ao calcular a rota.');
        dispatch({ type: 'SET_ROTA', payload: ROTA_INIT });
      } finally {
        loading.current = false;
        dispatch({ type: 'SET_UI', payload: { loadingRota: false } });
      }
    },
    [dispatch]
  );

  return { calcularRota };
}

// ─── Hook: SSE bridge FilaOrdens ───────────────────────────────────────────────
function useOSBridge(dispatch) {
  useEffect(() => {
    let source;
    let active = true;

    const connect = () => {
      if (!active) return;
      try {
        source = new EventSource('/api/ordens/eventos-fila', { withCredentials: true });

        source.addEventListener('nova_os_simulador', () => {
          dispatch({ type: 'FILA_BADGE_INC' });
        });

        source.onerror = () => {
          source.close();
        };
      } catch {
        // EventSource não suportado ou rota inexistente — ignora silenciosamente
      }
    };

    connect();
    return () => {
      active = false;
      source?.close();
    };
  }, [dispatch]);
}

// ─── Sub-componentes ───────────────────────────────────────────────────────────
function MapAutoFit({ polyline }) {
  const map = useMap();
  useEffect(() => {
    if (polyline?.length > 0) {
      map.fitBounds(L.latLngBounds(polyline).pad(0.15));
    }
  }, [polyline, map]);
  return null;
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span className="field-error">
      <FiAlertCircle size={12} /> {msg}
    </span>
  );
}

function StepBadge({ n, label, active, done }) {
  return (
    <div className={`step-badge ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
      <div className="step-circle">
        {done ? <FiCheckCircle size={14} /> : <span>{n}</span>}
      </div>
      <span className="step-label">{label}</span>
    </div>
  );
}

// ─── Modal: Cadastro Rápido de Cliente ────────────────────────────────────────
function QuickClientModal({ onClose, onClientCreated }) {
  const [data, setData] = useState({
    nome: '',
    telefone: '',
    email: '',
    endereco: '',
    cpf_cnpj: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.nome.trim() || !data.telefone.trim()) {
      toast.warn('Nome e Telefone são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/clients', data);
      toast.success('Cliente cadastrado!');
      onClientCreated({ ...data, id: res.data.id });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao cadastrar cliente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sim-overlay" onClick={onClose}>
      <div className="sim-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sim-modal-close" onClick={onClose}>
          <FiX />
        </button>
        <h3 className="sim-modal-title">
          <FiUserPlus /> Novo Cliente Rápido
        </h3>
        <form onSubmit={handleSubmit} className="sim-modal-form">
          {[
            ['Nome *', 'nome', 'text'],
            ['Telefone *', 'telefone', 'text'],
            ['E-mail', 'email', 'email'],
            ['Endereço', 'endereco', 'text'],
            ['CPF / CNPJ', 'cpf_cnpj', 'text'],
          ].map(([label, name, type]) => (
            <label key={name} className="sim-field-label">
              {label}
              <input
                type={type}
                name={name}
                value={data[name]}
                onChange={handleChange}
                className="sim-input"
                required={label.includes('*')}
              />
            </label>
          ))}
          <div className="sim-modal-actions">
            <button type="button" className="sim-btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="sim-btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Painel: Preview de OS ─────────────────────────────────────────────────────
function OSPreviewPanel({ calculo, form, rota, clienteSelecionado, clienteAvulso, savedQuote }) {
  if (!calculo) return null;

  const nomeCliente = clienteAvulso
    ? 'Cliente Avulso'
    : clienteSelecionado?.nome || '—';

  const veiculoTxt = calculo.veiculoLabel
    ? `${calculo.veiculoEmoji ? `${calculo.veiculoEmoji} ` : ''}${calculo.veiculoLabel}`
    : 'Serviço';
  const origemTxt = calculo.clienteLabel ? ` · ${calculo.clienteLabel}` : '';

  return (
    <div className="os-preview-panel">
      <div className="os-preview-header">
        <FiActivity size={14} />
        <span>Pré-visualização na FilaOrdens</span>
        {savedQuote && <span className="os-preview-uid"># {savedQuote.uid}</span>}
      </div>
      <div className="os-preview-body">
        <div className="os-preview-row">
          <FiTruck size={13} />
          <span>{veiculoTxt}{origemTxt}</span>
        </div>
        <div className="os-preview-row">
          <FiMapPin size={13} />
          <span className="os-preview-addr">
            {form.partida || '—'} <FiChevronRight size={11} /> {form.chegada || '—'}
          </span>
        </div>
        <div className="os-preview-row">
          <FiNavigation size={13} />
          <span>{rota.distanciaKm} km · {rota.tempoMinutos} min</span>
        </div>
        <div className="os-preview-row">
          <FiClock size={13} />
          <span>Cliente: {nomeCliente}</span>
        </div>
        <div className="os-preview-valor">
          <FiDollarSign size={15} />
          <strong>{fmtMoney(calculo.total)}</strong>
          <span className="os-preview-status">Na Fila</span>
        </div>
      </div>
    </div>
  );
}

// ─── Painel: Detalhamento financeiro (a partir da Calculadora) ──────────────────
function FinanceiroPanel({ calculo, calcDetalhe, form, dispatch }) {
  const temBreakdown = !!calculo.veiculoLabel; // veio com snapshot da calculadora

  return (
    <section className="sim-card">
      <div className="sim-card-header">
        <FiDollarSign className="sim-card-icon" />
        <h2>Detalhamento Financeiro</h2>
        <span className="sim-calc-source">via Calculadora</span>
      </div>

      <div className="sim-finance-table">
        {temBreakdown ? (
          <>
            <div className="sim-finance-row">
              <span>
                Bandeirada{' '}
                <em>{calculo.veiculoEmoji} {calculo.veiculoLabel}</em>
              </span>
              <span>R$ {f2(calculo.bandeirada)}</span>
            </div>

            {calculo.kmExc > 0 && (
              <div className="sim-finance-row">
                <span>
                  KM excedente{' '}
                  <em>({calculo.kmExc} km × R$ {f2(calculo.kmExcRate)}/km · franquia {calculo.franquia} km)</em>
                </span>
                <span>R$ {f2(calculo.valorKmExc)}</span>
              </div>
            )}

            {calculo.espera > 0 && (
              <div className="sim-finance-row">
                <span>
                  Tempo de espera{' '}
                  <em>({calculo.horasEspera}h × R$ {f2(calculo.taxaEspera)}/h)</em>
                </span>
                <span>R$ {f2(calculo.espera)}</span>
              </div>
            )}

            {(calculo.itensAdic || []).map((a) => (
              <div className="sim-finance-row" key={a.id}>
                <span>{a.emoji} {a.label}</span>
                <span>R$ {f2(a.valor)}</span>
              </div>
            ))}

            <div className="sim-finance-row sim-finance-row--sub">
              <strong>Subtotal</strong>
              <strong>R$ {f2(calculo.subtotal)}</strong>
            </div>

            {Math.abs(calculo.ajuste || 0) > 0.005 && (
              <div className="sim-finance-row">
                <span>
                  Ajuste de origem{' '}
                  <em>{calculo.clienteLabel} (× {f2(calculo.clienteMult)})</em>
                </span>
                <span>{calculo.ajuste < 0 ? '- ' : ''}R$ {f2(Math.abs(calculo.ajuste))}</span>
              </div>
            )}

            {(calculo.margemPct || 0) > 0 && (
              <div className="sim-finance-row">
                <span>
                  Margem do operador <em>({calculo.margemPct}%)</em>
                </span>
                <span>R$ {f2(calculo.margem)}</span>
              </div>
            )}
          </>
        ) : (
          <div className="sim-finance-row">
            <span>Valor calculado</span>
            <span>R$ {f2(calcDetalhe?.total)}</span>
          </div>
        )}

        <label className="sim-field-label-text sim-discount-label">Desconto (R$)</label>
        <input
          type="number"
          className="sim-input sim-discount-input"
          value={form.desconto}
          min={0}
          max={calcDetalhe?.total}
          onChange={(e) => dispatch({ type: 'SET_FORM', payload: { desconto: e.target.value } })}
        />

        <div className="sim-finance-row sim-finance-row--total">
          <strong>TOTAL</strong>
          <strong className="sim-total-value">{fmtMoney(calculo.total)}</strong>
        </div>
      </div>
    </section>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────
export default function SimuladorRefatorado({ user }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { form, rota, calcDetalhe, clienteSelecionado, clienteAvulso,
          savedQuote, clientes, historico, tenantInfo, errors, ui } = state;

  const isAdmin = user && ['admin_geral', 'admin'].includes(user.perfil);

  // ─── Hooks ──────────────────────────────────────────────────────────────────
  const { fetchHistorico } = useSimuladorData(dispatch);
  const { calcularRota } = useRotaCalculator(dispatch);
  useOSBridge(dispatch);

  // ─── Cálculo final = (valor da calculadora) − desconto do simulador ───────────
  const calculo = useMemo(() => {
    if (!calcDetalhe) return null;
    const desconto = parseFloat(form.desconto) || 0;
    const total = Math.max(0, (calcDetalhe.total || 0) - desconto);
    return { ...calcDetalhe, desconto, total };
  }, [calcDetalhe, form.desconto]);

  // ─── Recebe o valor aplicado pela Calculadora ─────────────────────────────────
  const handleAplicarPreco = useCallback((total, detalhe) => {
    // `detalhe` traz o snapshot completo (veículo, adicionais, margem etc.).
    // Se vier só o total (versão antiga da calculadora), ainda funciona.
    const base = detalhe && typeof detalhe === 'object'
      ? { origem: 'calculadora', ...detalhe }
      : { origem: 'calculadora', total: parseFloat(total) || 0 };

    dispatch({ type: 'SET_CALC_DETALHE', payload: base });
    dispatch({ type: 'SET_UI', payload: { calcModalOpen: false } });
    toast.success(`Valor de ${fmtMoney(base.total)} aplicado à cotação.`);
  }, []);

  // ─── CEP ────────────────────────────────────────────────────────────────────
  const handleBuscaCep = useCallback(async (cep, campo) => {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error('CEP não encontrado.');
        return;
      }
      dispatch({
        type: 'SET_FORM',
        payload: {
          [campo]: `${data.logradouro}, ${data.bairro}, ${data.localidade} - ${data.uf}`,
        },
      });
      toast.success('Endereço preenchido!');
    } catch {
      toast.error('Falha ao buscar CEP.');
    }
  }, []);

  // ─── Salvar cotação ──────────────────────────────────────────────────────────
  const handleSalvar = useCallback(async () => {
    if (!calculo) { toast.warn('Precifique a cotação na calculadora primeiro.'); return; }
    if (!clienteAvulso && !clienteSelecionado) {
      dispatch({ type: 'SET_ERRORS', payload: { cliente: 'Selecione um cliente ou marque como avulso.' } });
      toast.warn('Selecione um cliente ou marque como avulso.');
      return;
    }
    dispatch({ type: 'SET_UI', payload: { loadingSave: true } });
    try {
      const res = await api.post('/api/orcamentos', {
        cliente_id: clienteSelecionado?.value ?? null,
        // calcDetalhe = valor BASE (antes do desconto). desconto vive em formData.
        dados_cotacao: JSON.stringify({ formData: form, rotaInfo: rota, calcDetalhe }),
        valor_total: calculo.total,
      });
      dispatch({ type: 'SET_SAVED_QUOTE', payload: { id: res.data.id, uid: res.data.uid } });
      toast.success(`Cotação ${res.data.uid} salva!`);
      fetchHistorico();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar cotação.');
    } finally {
      dispatch({ type: 'SET_UI', payload: { loadingSave: false } });
    }
  }, [calculo, calcDetalhe, clienteAvulso, clienteSelecionado, form, rota, fetchHistorico]);

  // ─── Converter em OS ────────────────────────────────────────────────────────
  const handleConverterOS = useCallback(async () => {
    if (!savedQuote) { toast.error('Salve a cotação primeiro.'); return; }

    let nomeAvulso = null;
    if (clienteAvulso) {
      nomeAvulso = window.prompt('Nome do cliente avulso para a OS:');
      if (!nomeAvulso?.trim()) { toast.warn('Conversão cancelada.'); return; }
    }

    const placa = window.prompt('Placa do veículo do cliente:');
    if (!placa?.trim()) { toast.warn('Conversão cancelada. Placa é obrigatória.'); return; }

    dispatch({ type: 'SET_UI', payload: { loadingConverter: true } });
    try {
      const res = await api.post(`/api/orcamentos/${savedQuote.id}/converter-os`, {
        nome_cliente_avulso: nomeAvulso?.trim() ?? null,
        placa_veiculo: placa.trim().toUpperCase(),
      });
      toast.success(res.data.message);
      dispatch({ type: 'NOVA_COTACAO' });
      fetchHistorico();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao converter cotação em OS.');
    } finally {
      dispatch({ type: 'SET_UI', payload: { loadingConverter: false } });
    }
  }, [savedQuote, clienteAvulso, fetchHistorico]);

  // ─── Carregar cotação do histórico ──────────────────────────────────────────
  const handleCarregarHistorico = useCallback(
    async (id) => {
      try {
        const res = await api.get(`/api/orcamentos/${id}`);
        const orc = res.data;

        // dados_cotacao pode vir como string (coluna TEXT) ou já como objeto
        // (coluna JSON — o driver mysql2 entrega parseado). Trata os dois casos.
        const dados = typeof orc.dados_cotacao === 'string'
          ? (JSON.parse(orc.dados_cotacao) || {})
          : (orc.dados_cotacao || {});

        // Compatibilidade: cotações novas guardam `calcDetalhe` (base);
        // cotações antigas guardavam `calculoDetalhado` (já com desconto).
        const detalhe = dados.calcDetalhe || dados.calculoDetalhado || null;
        const legacy = !dados.calcDetalhe && !!dados.calculoDetalhado;

        dispatch({
          type: 'SET_FORM',
          payload: { ...(dados.formData || {}), ...(legacy ? { desconto: 0 } : {}) },
        });
        dispatch({ type: 'SET_ROTA', payload: dados.rotaInfo || ROTA_INIT });
        dispatch({ type: 'SET_CALC_DETALHE', payload: detalhe });
        dispatch({ type: 'SET_SAVED_QUOTE', payload: { id: orc.id, uid: orc.orcamento_uid } });

        if (orc.cliente_id) {
          const c = clientes.find((cl) => cl.value === orc.cliente_id) ?? null;
          dispatch({ type: 'SET_CLIENTE', payload: c });
          dispatch({ type: 'SET_CLIENTE_AVULSO', payload: false });
        } else {
          dispatch({ type: 'SET_CLIENTE', payload: null });
          dispatch({ type: 'SET_CLIENTE_AVULSO', payload: true });
        }

        toast.success(`Cotação ${orc.orcamento_uid} carregada.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        console.error('[CARREGAR COTACAO]', err);
        toast.error('Falha ao carregar cotação.');
      }
    },
    [clientes]
  );

  // ─── Excluir cotação ────────────────────────────────────────────────────────
  const handleDeletar = useCallback(
    async (e, id) => {
      e.stopPropagation();
      if (!window.confirm('Excluir esta cotação? Se houver OS vinculada ela também será removida.')) return;
      try {
        await api.delete(`/api/orcamentos/${id}`);
        dispatch({ type: 'SET_HISTORICO', payload: historico.filter((h) => h.id !== id) });
        toast.success('Cotação excluída.');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Falha ao excluir.');
      }
    },
    [historico]
  );

  // ─── Gerar PDF ──────────────────────────────────────────────────────────────
  const handleGerarPDF = useCallback(() => {
    if (!calculo) return toast.warn('Precifique a cotação antes de gerar o PDF.');

    const money = (v) => `R$ ${f2(v)}`;
    const doc = new jsPDF();
    const pw  = doc.internal.pageSize.getWidth();
    const m   = 14;
    const primary = '#101C5D';
    const accent  = '#FF8C00';

    const ti          = tenantInfo || {};
    const nomeEmpresa = ti.nome_empresa || '';
    const emailEmpresa= ti.email        || '';
    const telEmpresa  = ti.telefone     || '';

    // ── Cabeçalho ──────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(primary);
    doc.text(nomeEmpresa, m, 20);

    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor('#555');
    let yInfo = 15;
    if (telEmpresa)   { doc.text(telEmpresa,   pw - m, yInfo, { align: 'right' }); yInfo += 5; }
    if (emailEmpresa) { doc.text(emailEmpresa, pw - m, yInfo, { align: 'right' }); }

    doc.setFontSize(16).setFont('helvetica', 'bold').setTextColor(primary);
    doc.text('ORÇAMENTO DE SERVIÇO', pw / 2, 45, { align: 'center' });
    doc.setDrawColor(accent).line(m, 52, pw - m, 52);

    const clienteNome = clienteAvulso
      ? 'Cliente Avulso'
      : clienteSelecionado
      ? `${clienteSelecionado.nome}${clienteSelecionado.cpf_cnpj ? ` (${clienteSelecionado.cpf_cnpj})` : ''}`
      : '—';

    const dataValidade = new Date();
    dataValidade.setDate(dataValidade.getDate() + 2);

    doc.setFontSize(10).setFont('helvetica', 'normal').setTextColor('#555');
    doc.text('Cliente:', m, 62); doc.setFont('helvetica', 'bold').setTextColor('#222').text(clienteNome, m + 17, 62);
    doc.setFont('helvetica', 'normal').setTextColor('#555');
    doc.text('Data:', m, 68); doc.setFont('helvetica', 'bold').setTextColor('#222').text(new Date().toLocaleDateString('pt-BR'), m + 12, 68);
    doc.setFont('helvetica', 'normal').setTextColor('#555');
    doc.text('Nº:', pw - m - 50, 62); doc.setFont('helvetica', 'bold').setTextColor('#222').text(savedQuote?.uid || 'RASCUNHO', pw - m, 62, { align: 'right' });
    doc.setFont('helvetica', 'normal').setTextColor('#555');
    doc.text('Válido até:', pw - m - 50, 68); doc.setFont('helvetica', 'bold').setTextColor('#222').text(dataValidade.toLocaleDateString('pt-BR'), pw - m, 68, { align: 'right' });

    doc.setFontSize(12).setFont('helvetica', 'bold').setTextColor(primary);
    doc.text('Detalhes da Rota', m, 82);

    autoTable(doc, {
      startY: 85,
      head: [['Descrição', 'Detalhe']],
      body: [
        ['Local de Partida', form.partida],
        ['Local de Chegada', form.chegada],
        ['Distância', `${rota.distanciaKm.toFixed(2)} km`],
        ['Tempo Estimado', `${rota.tempoMinutos} min`],
        ...(calculo.veiculoLabel ? [['Veículo', `${calculo.veiculoLabel}`]] : []),
      ],
      theme: 'grid',
      headStyles: { fillColor: '#F5F5F5', textColor: '#555', fontStyle: 'bold' },
      styles: { textColor: '#222', lineColor: '#E0E0E0', lineWidth: 0.1 },
      columnStyles: { 0: { cellWidth: 55 } },
    });

    const y1 = doc.lastAutoTable.finalY;
    doc.setFontSize(12).setFont('helvetica', 'bold').setTextColor(primary);
    doc.text('Detalhamento Financeiro', m, y1 + 14);

    // Corpo do financeiro montado a partir do que a CALCULADORA definiu
    const body = [];
    if (calculo.veiculoLabel) {
      body.push(['Bandeirada', calculo.veiculoLabel, money(calculo.bandeirada)]);
      if (calculo.kmExc > 0) {
        body.push(['KM excedente', `${calculo.kmExc} km × ${money(calculo.kmExcRate)}/km`, money(calculo.valorKmExc)]);
      }
      if (calculo.espera > 0) {
        body.push(['Tempo de espera', `${calculo.horasEspera}h × ${money(calculo.taxaEspera)}/h`, money(calculo.espera)]);
      }
      (calculo.itensAdic || []).forEach((a) => {
        body.push(['Adicional', a.label, money(a.valor)]);
      });
      body.push(['Subtotal', '', money(calculo.subtotal)]);
      if (Math.abs(calculo.ajuste || 0) > 0.005) {
        body.push([`Ajuste origem (${calculo.clienteLabel})`, `× ${f2(calculo.clienteMult)}`, money(calculo.ajuste)]);
      }
      if ((calculo.margemPct || 0) > 0) {
        body.push([`Margem do operador (${calculo.margemPct}%)`, '', money(calculo.margem)]);
      }
    } else {
      body.push(['Valor calculado', '', money(calcDetalhe?.total)]);
    }
    if ((calculo.desconto || 0) > 0) {
      body.push(['Desconto', '', `- ${money(calculo.desconto)}`]);
    }

    autoTable(doc, {
      startY: y1 + 17,
      head: [['Item', 'Descrição', 'Valor (R$)']],
      body,
      foot: [['TOTAL', '', money(calculo.total)]],
      theme: 'striped',
      headStyles: { fillColor: primary, textColor: '#fff' },
      footStyles: { fillColor: accent, textColor: '#fff', fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'right' } },
    });

    const y2 = doc.lastAutoTable.finalY;
    doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor('#999');
    doc.text('— Orçamento válido por 48 horas. Valores sujeitos a alteração conforme condições do serviço. —', pw / 2, y2 + 18, { align: 'center' });

    doc.save(`orcamento_${savedQuote?.uid || 'rascunho'}.pdf`);
  }, [calculo, calcDetalhe, form, rota, clienteAvulso, clienteSelecionado, savedQuote, tenantInfo]);

  // ─── Etapas do fluxo ────────────────────────────────────────────────────────
  const step1Done = !!(
    (clienteAvulso || clienteSelecionado) &&
    form.partida &&
    form.chegada
  );
  const step2Done = rota.distanciaKm > 0;
  const step3Done = !!calculo;
  const step4Done = !!savedQuote;

  const abrirCalculadora = () => dispatch({ type: 'SET_UI', payload: { calcModalOpen: true } });

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (!user) return <div className="sim-loading"><FiZap /> Carregando...</div>;

  return (
    <div className="sim-root">
      {/* Modais */}
      {ui.calcModalOpen && (
        <CalculadoraPreco
          isAdmin={isAdmin}
          kmInicial={rota.distanciaKm}
          onClose={() => dispatch({ type: 'SET_UI', payload: { calcModalOpen: false } })}
          onAplicar={handleAplicarPreco}
        />
      )}
      {ui.clientModalOpen && (
        <QuickClientModal
          onClose={() => dispatch({ type: 'SET_UI', payload: { clientModalOpen: false } })}
          onClientCreated={(c) => {
            const fmt = {
              value: c.id,
              label: `${c.nome}${c.cpf_cnpj ? ` · ${c.cpf_cnpj}` : ''}`,
              ...c,
            };
            dispatch({ type: 'ADD_CLIENTE_LISTA', payload: fmt });
            dispatch({ type: 'SET_UI', payload: { clientModalOpen: false } });
          }}
        />
      )}
      <EnviarModal
        isOpen={ui.sendModalOpen}
        onClose={() => dispatch({ type: 'SET_UI', payload: { sendModalOpen: false } })}
        quoteData={{ id: savedQuote?.id, uid: savedQuote?.uid, cliente: clienteSelecionado }}
      />

      {/* ── Cabeçalho ── */}
      <header className="sim-header">
        <div className="sim-header-left">
          <FiTruck className="sim-header-icon" />
          <div>
            <h1 className="sim-title">Centro de Cotação</h1>
            <p className="sim-subtitle">Simulador · Despacho · Fila de Ordens</p>
          </div>
        </div>
        <div className="sim-header-right">
          {ui.filaBadge > 0 && (
            <button
              className="sim-fila-badge"
              onClick={() => {
                dispatch({ type: 'FILA_BADGE_RESET' });
                navigate('/fila-ordens');
              }}
            >
              <FiActivity size={13} />
              <strong>{ui.filaBadge}</strong> nova{ui.filaBadge > 1 ? 's' : ''} OS na fila
              <FiChevronRight size={12} />
            </button>
          )}
          <button className="sim-btn-icon" onClick={abrirCalculadora}>
            <FiDollarSign size={14} /> Calculadora
          </button>
        </div>
      </header>

      {/* ── Progress Steps ── */}
      <div className="sim-steps">
        <StepBadge n={1} label="Cliente & Rota" active={!step1Done} done={step1Done} />
        <div className="sim-steps-divider" />
        <StepBadge n={2} label="Calcular Rota" active={step1Done && !step2Done} done={step2Done} />
        <div className="sim-steps-divider" />
        <StepBadge n={3} label="Precificar" active={step2Done && !step3Done} done={step3Done} />
        <div className="sim-steps-divider" />
        <StepBadge n={4} label="Salvar & Despachar" active={step3Done && !step4Done} done={step4Done} />
      </div>

      {/* ── Corpo principal ── */}
      <div className="sim-body">
        {/* ╔══ COLUNA ESQUERDA ══╗ */}
        <div className="sim-col sim-col-left">

          {/* Card 1: Cliente */}
          <section className="sim-card">
            <div className="sim-card-header">
              <FiUserPlus className="sim-card-icon" />
              <h2>Cliente</h2>
            </div>

            <label className="sim-toggle-row">
              <div className="sim-toggle-track">
                <input
                  type="checkbox"
                  checked={clienteAvulso}
                  onChange={(e) =>
                    dispatch({ type: 'SET_CLIENTE_AVULSO', payload: e.target.checked })
                  }
                />
                <span className="sim-toggle-thumb" />
              </div>
              <span>Cliente Avulso (sem cadastro)</span>
            </label>

            {!clienteAvulso && (
              <div className="sim-field">
                <div className="sim-field-header">
                  <label className="sim-field-label-text">Selecionar Cliente</label>
                  <button
                    className="sim-btn-link"
                    onClick={() => dispatch({ type: 'SET_UI', payload: { clientModalOpen: true } })}
                  >
                    <FiPlus size={12} /> Novo
                  </button>
                </div>
                <Select
                  options={clientes}
                  value={clienteSelecionado}
                  onChange={(c) => dispatch({ type: 'SET_CLIENTE', payload: c })}
                  isSearchable
                  placeholder="Digite para buscar..."
                  classNamePrefix="sim-select"
                  noOptionsMessage={() => 'Nenhum cliente encontrado'}
                />
                <FieldError msg={errors.cliente} />
              </div>
            )}
          </section>

          {/* Card 2: Endereços */}
          <section className="sim-card">
            <div className="sim-card-header">
              <FiMapPin className="sim-card-icon" />
              <h2>Endereços</h2>
            </div>

            {/* Partida */}
            <div className="sim-addr-group">
              <div className="sim-addr-label">
                <span className="sim-addr-dot sim-addr-dot--green" />
                Partida
              </div>

              <div className="sim-field sim-addr-street">
                <label className="sim-field-label-text">Rua / Bairro / Cidade</label>
                <input
                  type="text"
                  className={`sim-input ${errors.partida ? 'sim-input--error' : ''}`}
                  name="partida"
                  value={form.partida}
                  placeholder="Ex: Av. Paulista, Bela Vista, São Paulo - SP"
                  onChange={(e) => dispatch({ type: 'SET_FORM', payload: { partida: e.target.value } })}
                />
                <FieldError msg={errors.partida} />
              </div>

              <div className="sim-addr-meta">
                <div className="sim-field">
                  <label className="sim-field-label-text">CEP</label>
                  <input
                    type="text"
                    className="sim-input"
                    name="cepPartida"
                    value={form.cepPartida}
                    maxLength={9}
                    placeholder="00000-000"
                    onChange={(e) => dispatch({ type: 'SET_FORM', payload: { cepPartida: e.target.value } })}
                    onBlur={(e) => handleBuscaCep(e.target.value, 'partida')}
                  />
                </div>
                <div className="sim-field">
                  <label className="sim-field-label-text">Nº</label>
                  <input
                    type="text"
                    className={`sim-input ${errors.numeroPartida ? 'sim-input--error' : ''}`}
                    name="numeroPartida"
                    value={form.numeroPartida}
                    placeholder="100"
                    onChange={(e) => dispatch({ type: 'SET_FORM', payload: { numeroPartida: e.target.value } })}
                  />
                  <FieldError msg={errors.numeroPartida} />
                </div>
              </div>
            </div>

            {/* Chegada */}
            <div className="sim-addr-group sim-addr-group--mt">
              <div className="sim-addr-label">
                <span className="sim-addr-dot sim-addr-dot--orange" />
                Chegada
              </div>

              <div className="sim-field sim-addr-street">
                <label className="sim-field-label-text">Rua / Bairro / Cidade</label>
                <input
                  type="text"
                  className={`sim-input ${errors.chegada ? 'sim-input--error' : ''}`}
                  name="chegada"
                  value={form.chegada}
                  placeholder="Ex: Rua Oscar Freire, Jardins, São Paulo - SP"
                  onChange={(e) => dispatch({ type: 'SET_FORM', payload: { chegada: e.target.value } })}
                />
                <FieldError msg={errors.chegada} />
              </div>

              <div className="sim-addr-meta">
                <div className="sim-field">
                  <label className="sim-field-label-text">CEP</label>
                  <input
                    type="text"
                    className="sim-input"
                    name="cepChegada"
                    value={form.cepChegada}
                    maxLength={9}
                    placeholder="00000-000"
                    onChange={(e) => dispatch({ type: 'SET_FORM', payload: { cepChegada: e.target.value } })}
                    onBlur={(e) => handleBuscaCep(e.target.value, 'chegada')}
                  />
                </div>
                <div className="sim-field">
                  <label className="sim-field-label-text">Nº</label>
                  <input
                    type="text"
                    className={`sim-input ${errors.numeroChegada ? 'sim-input--error' : ''}`}
                    name="numeroChegada"
                    value={form.numeroChegada}
                    placeholder="500"
                    onChange={(e) => dispatch({ type: 'SET_FORM', payload: { numeroChegada: e.target.value } })}
                  />
                  <FieldError msg={errors.numeroChegada} />
                </div>
              </div>
            </div>

            <button
              className="sim-btn-calc"
              disabled={ui.loadingRota}
              onClick={() => calcularRota(form)}
            >
              {ui.loadingRota ? (
                <><span className="sim-spinner" /> Calculando...</>
              ) : (
                <><FiNavigation /> Buscar Rota &amp; Calcular</>
              )}
            </button>
          </section>

          {/* Card 3: Histórico */}
          <section className="sim-card">
            <div className="sim-card-header">
              <FiClock className="sim-card-icon" />
              <h2>Últimas Cotações</h2>
              <button
                className="sim-btn-link sim-btn-link--right"
                onClick={fetchHistorico}
              >
                <FiRefreshCw size={12} /> Atualizar
              </button>
            </div>

            {historico.length === 0 ? (
              <p className="sim-empty-msg">Nenhuma cotação ainda.</p>
            ) : (
              <ul className="sim-historico-list">
                {historico.map((h) => (
                  <li
                    key={h.id}
                    className="sim-historico-item"
                    onClick={() => handleCarregarHistorico(h.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleCarregarHistorico(h.id)}
                  >
                    <div className="sim-historico-info">
                      <strong>{h.orcamento_uid}</strong>
                      <span>{h.nome_cliente || 'Cliente Avulso'}</span>
                      <small>{new Date(h.criado_em).toLocaleDateString('pt-BR')}</small>
                    </div>
                    <div className="sim-historico-right">
                      <strong>{fmtMoney(h.valor_total)}</strong>
                      <span className={`sim-tag sim-tag--${(h.status || '').toLowerCase()}`}>
                        {h.status}
                      </span>
                      {isAdmin && (
                        <button
                          className="sim-btn-del"
                          onClick={(e) => handleDeletar(e, h.id)}
                          title="Excluir cotação"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* ╔══ COLUNA DIREITA ══╗ */}
        <div className="sim-col sim-col-right">
          {/* Placeholder só quando ainda não há rota E não há cálculo */}
          {rota.distanciaKm === 0 && !calculo && (
            <div className="sim-placeholder">
              <FiNavigation size={36} className="sim-placeholder-icon" />
              <h3>Aguardando cálculo</h3>
              <p>Preencha os dados à esquerda e clique em <strong>Buscar Rota</strong> para visualizar o mapa e, em seguida, precificar pela <strong>Calculadora</strong>.</p>
            </div>
          )}

          {/* Mapa — aparece assim que a rota é calculada */}
          {rota.distanciaKm > 0 && (
            <>
              <section className="sim-card sim-card--map">
                <div className="sim-card-header">
                  <FiMapPin className="sim-card-icon" />
                  <h2>Rota no Mapa</h2>
                  <div className="sim-route-stats">
                    <span><FiNavigation size={11} /> {rota.distanciaKm} km</span>
                    <span><FiClock size={11} /> {rota.tempoMinutos} min</span>
                  </div>
                </div>
                <div className="sim-map-wrap">
                  <MapContainer
                    center={rota.partidaCoords ?? [-14.23, -51.92]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                      attribution='© <a href="https://mapbox.com">Mapbox</a>'
                      tileSize={512}
                      zoomOffset={-1}
                    />
                    {rota.partidaCoords && <Marker position={rota.partidaCoords} icon={iconPartida} />}
                    {rota.chegadaCoords && <Marker position={rota.chegadaCoords} icon={iconChegada} />}
                    {rota.polyline.length > 0 && (
                      <Polyline
                        positions={rota.polyline}
                        color="#f97316"
                        weight={4}
                        opacity={0.9}
                      />
                    )}
                    <MapAutoFit polyline={rota.polyline} />
                  </MapContainer>
                </div>
              </section>

              {/* Precificação */}
              {calculo ? (
                <>
                  <FinanceiroPanel
                    calculo={calculo}
                    calcDetalhe={calcDetalhe}
                    form={form}
                    dispatch={dispatch}
                  />

                  <OSPreviewPanel
                    calculo={calculo}
                    form={form}
                    rota={rota}
                    clienteSelecionado={clienteSelecionado}
                    clienteAvulso={clienteAvulso}
                    savedQuote={savedQuote}
                  />

                  {/* Ações */}
                  <section className="sim-card sim-card--actions">
                    <div className="sim-card-header">
                      <FiZap className="sim-card-icon" />
                      <h2>Ações</h2>
                      <button className="sim-btn-link sim-btn-link--right" onClick={abrirCalculadora}>
                        <FiDollarSign size={12} /> Recalcular
                      </button>
                    </div>
                    <div className="sim-actions-grid">
                      <button
                        className="sim-action-btn sim-action-btn--primary"
                        onClick={handleSalvar}
                        disabled={ui.loadingSave || (!clienteAvulso && !clienteSelecionado)}
                      >
                        <FiSave />
                        <span>{ui.loadingSave ? 'Salvando...' : 'Salvar Cotação'}</span>
                      </button>
                      <button className="sim-action-btn sim-action-btn--secondary" onClick={handleGerarPDF}>
                        <FiFileText /><span>Gerar PDF</span>
                      </button>
                      <button
                        className="sim-action-btn sim-action-btn--secondary"
                        disabled={!savedQuote}
                        onClick={() => dispatch({ type: 'SET_UI', payload: { sendModalOpen: true } })}
                      >
                        <FiSend /><span>Enviar</span>
                      </button>
                      <button
                        className="sim-action-btn sim-action-btn--success"
                        disabled={!savedQuote || ui.loadingConverter}
                        onClick={handleConverterOS}
                      >
                        <FiTruck />
                        <span>{ui.loadingConverter ? 'Despachando...' : 'Virar OS'}</span>
                      </button>
                      <button
                        className="sim-action-btn sim-action-btn--outline sim-action-btn--full"
                        onClick={() => dispatch({ type: 'NOVA_COTACAO' })}
                      >
                        <FiPlus /><span>Nova Cotação</span>
                      </button>
                    </div>
                    {savedQuote && (
                      <div className="sim-saved-badge">
                        <FiCheckCircle size={14} />
                        Cotação <strong>{savedQuote.uid}</strong> salva — pronta para despacho
                      </div>
                    )}
                  </section>
                </>
              ) : (
                /* Rota calculada mas ainda sem precificação */
                <section className="sim-card sim-calc-cta">
                  <div className="sim-card-header">
                    <FiDollarSign className="sim-card-icon" />
                    <h2>Precificação</h2>
                  </div>
                  <p className="sim-calc-cta-text">
                    A rota está pronta (<strong>{rota.distanciaKm} km</strong>). Abra a calculadora
                    para precificar esta cotação com base nas regras que você definiu —
                    veículo, adicionais, origem do cliente e margem.
                  </p>
                  <button className="sim-btn-calc" onClick={abrirCalculadora}>
                    <FiDollarSign /> Abrir Calculadora
                  </button>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}